import React, { useState } from "react";
import ActionAccordion from "../components/ActionAccordion";
import { STRENGTH_THRESHOLD } from "../utils/diagnosticHelpers";

/**
 * Results View Component
 * Shows submitted form results grouped by actions with skill/attitude entries
 */
const ResultsView = ({ submission, assignment, aggregatedData, isLoadingAggregated, onBack }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Actions arrive pre-scored and sorted by the backend, strongest first.
  const groupedActions = aggregatedData?.actions || [];

  // Split on the rater-relative score, not the raw one: raters rarely go below 4,
  // so a raw cutoff would leave Growth Opportunities permanently empty.
  const strengthActions = groupedActions.filter(
    (a) => a.centeredAverage >= STRENGTH_THRESHOLD
  );
  const growthActions = groupedActions.filter(
    (a) => a.centeredAverage < STRENGTH_THRESHOLD
  );

  return (
    <div className="diagnostic-card results-card">
      <div className="card-head">
        <h2 className="section-heading">Diagnostic Results</h2>
        <div className="step-indicator">Completed</div>
      </div>

      <div className="results-info">
        <p className="results-meta">
          <strong>Form:</strong> {submission.templateTitle}
        </p>
        <p className="results-meta">
          <strong>Type:</strong>{" "}
          {submission.evaluationType === "self"
            ? "Self Evaluation"
            : `Colleague Evaluation of ${submission.target?.firstName} ${submission.target?.lastName}`}
        </p>
        <p className="results-meta">
          <strong>Submitted:</strong>{" "}
          {new Date(submission.submittedAt).toLocaleString()}
        </p>
      </div>

      {/* Aggregated Tally Section */}
      {isLoadingAggregated ? (
        <div className="loading-state">Loading aggregated results...</div>
      ) : aggregatedData?.overallStats ? (
        <div className="aggregated-tally-section">
          {/* Overall Stats */}
          <div className="overall-stats-banner">
            <div className="stat-item-main">
              <span className="stat-value-large">
                {aggregatedData.overallStats.overallAverage}/5
              </span>
              <span className="stat-label">Overall Average</span>
            </div>
            <div className="stat-item-main">
              <span className="stat-value-large">
                {groupedActions.length}
              </span>
              <span className="stat-label">Actions Evaluated</span>
            </div>
            <div className="stat-item-main">
              <span className="stat-value-large">
                {(aggregatedData.overallStats.uniqueRespondents?.self || 0) +
                  (aggregatedData.overallStats.uniqueRespondents?.colleague || 0)}
              </span>
              <span className="stat-label">Respondents</span>
            </div>
          </div>

          <p className="results-explainer">
            <strong>How this is calculated:</strong> Each action is measured
            relative to the rater's own average score. Actions falling below that
            benchmark are identified as growth opportunities.
          </p>

          {/* Strengths and Growth Opportunities Columns */}
          <div className="results-columns">
            {/* Strengths Column */}
            <div className="results-column strengths-column">
              <h3 className="column-title">
                <span className="column-icon">✓</span> Strengths
              </h3>

              {strengthActions.length > 0 ? (
                strengthActions.map((actionData, idx) => (
                  <div key={idx} className="action-result-card strength">
                    <div className="action-result-header">
                      <span className="action-result-name">{actionData.action.actionName}</span>
                      <span className="action-result-score">{actionData.average.toFixed(1)}/5</span>
                    </div>
                    <ActionAccordion
                      entries={actionData.action.entries || []}
                      actionId={actionData.action.actionId}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                    />
                  </div>
                ))
              ) : (
                <div className="placeholder-text">Not enough feedback to compare actions yet</div>
              )}
            </div>

            {/* Growth Opportunities Column */}
            <div className="results-column growth-column">
              <h3 className="column-title">
                <span className="column-icon">↗</span> Growth Opportunities
              </h3>

              {growthActions.length > 0 ? (
                growthActions.map((actionData, idx) => (
                  <div key={idx} className="action-result-card growth">
                    <div className="action-result-header">
                      <div className="action-result-info">
                        <span className="action-result-name">{actionData.action.actionName}</span>
                        {/* The score can still look high on its own; this column is
                            relative, so say what put the action here. */}
                        <span className="action-result-context">Rated below your average</span>
                      </div>
                      <span className="action-result-score">{actionData.average.toFixed(1)}/5</span>
                    </div>
                    <ActionAccordion
                      entries={actionData.action.entries || []}
                      actionId={actionData.action.actionId}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                    />
                  </div>
                ))
              ) : (
                <div className="placeholder-text">Not enough feedback to compare actions yet</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="no-aggregated-data">
          <p>No aggregated feedback available yet.</p>
        </div>
      )}

      <div className="nav-row">
        <button type="button" className="ghost-btn" onClick={onBack}>
          ← Back to Forms
        </button>
      </div>
    </div>
  );
};

export default ResultsView;
